import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { trackAffiliatePageViewed, trackAffiliateApplyStarted } from '../utils/analytics';

const STEPS = [
  {
    num: '1',
    icon: '📝',
    title: 'Apply in minutes',
    desc: 'Fill out a short application — no traffic minimums, no follower requirements. We review every application personally.',
  },
  {
    num: '2',
    icon: '🔗',
    title: 'Share your unique link',
    desc: 'Get your personal referral link and promo code. Share them on Instagram, TikTok, YouTube, your blog — anywhere your audience hangs out.',
  },
  {
    num: '3',
    icon: '💸',
    title: 'Earn recurring commission',
    desc: 'Earn up to 30% commission on every paying customer you refer — for up to 12 months per customer. Not just the first payment.',
  },
  {
    num: '4',
    icon: '🏦',
    title: 'Get paid via Stripe',
    desc: 'Connect your bank account through Stripe. Once your balance reaches $25, payouts go directly to your bank within 1–2 business days.',
  },
];

const PERKS = [
  { icon: '♾️', title: 'Recurring commissions', desc: 'Earn on every renewal — up to 12 months per customer, not just the first sale.' },
  { icon: '🎟️', title: 'Promo code for your audience', desc: 'Your followers get a discount at checkout. Win for them, win for you.' },
  { icon: '📊', title: 'Real-time dashboard', desc: 'Track clicks, signups, conversions, and pending earnings — live.' },
  { icon: '🌍', title: 'Open worldwide', desc: 'We support affiliates in 26+ countries with direct bank payouts via Stripe.' },
  { icon: '🚀', title: 'No minimums to apply', desc: 'No follower threshold. If you have an audience that loves kids\' content, apply.' },
  { icon: '🔒', title: 'Reliable tracking', desc: '30-day cookie window. Conversions are tracked and attributed accurately.' },
];

const FAQS = [
  {
    q: 'How much do I earn per referral?',
    a: 'You earn up to 30% commission on every subscription payment your referred customers make — for up to 12 months. So if someone you refer stays subscribed for a year, you earn commission on every one of their payments.',
  },
  {
    q: 'When and how do I get paid?',
    a: 'Once your balance reaches $25, you can request a payout. Payouts go directly to your bank account via Stripe within 1–2 business days (7–14 days for your very first payout while Stripe verifies your account).',
  },
  {
    q: 'Do I need a large following to apply?',
    a: 'No. We don\'t have a minimum follower count. We look at the quality and relevance of your audience — parents, educators, or anyone interested in children\'s content are a great fit.',
  },
  {
    q: 'What is the cookie duration?',
    a: 'When someone clicks your referral link, we track them for 30 days. If they subscribe within that window, you earn the commission.',
  },
  {
    q: 'Does my audience get a discount?',
    a: 'Yes. You\'ll get a personal promo code your audience can use at checkout for a discount on their first month. It\'s a great incentive to share.',
  },
  {
    q: 'Which countries are supported?',
    a: 'We support affiliates in 26+ countries including the US, UK, Canada, Australia, and most of Europe. Payouts are handled through Stripe Connect.',
  },
  {
    q: 'Can I see my stats in real time?',
    a: 'Yes. Your affiliate dashboard shows clicks, signups, conversions, commission earned, and payout history — all updated in real time.',
  },
  {
    q: 'Is there a contract or lock-in?',
    a: 'No contracts, no lock-in. You can pause or leave the program at any time. Earned commissions are always paid out.',
  },
];

const AffiliateLandingPage = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    trackAffiliatePageViewed();
  }, []);

  const handleApplyCta = () => {
    trackAffiliateApplyStarted();
    if (user) {
      navigate('/affiliate/apply');
    } else {
      navigate('/register?next=/affiliate/apply');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="Affiliate Program — Earn up to 30% recurring commission"
        description="Join the Dreamy Bedtime affiliate program. Earn up to 30% recurring commission for every family you refer. No follower minimums. Real-time dashboard. Paid via Stripe."
        canonical="/affiliate"
      />
      <Navbar />

      {/* ── Hero ── */}
      <section className="bg-dream text-white text-center" style={{ padding: '72px 24px 60px' }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill fw-semibold" style={{ fontSize: '13px' }}>
            💰 Affiliate Program
          </span>
          <h1 className="fw-bold mb-4" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(32px,6vw,54px)', lineHeight: 1.15 }}>
            Earn recurring commission<br />sharing stories you love
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '18px', lineHeight: 1.7, marginBottom: '36px' }}>
            Recommend Dreamy Bedtime to parents and educators. Earn <strong style={{ color: '#fff' }}>up to 30% commission</strong> on every payment — for up to 12 months per customer.
          </p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button
              className="btn btn-warning fw-bold px-5 py-3 rounded-pill"
              style={{ fontSize: '16px' }}
              onClick={handleApplyCta}
            >
              Apply Now — It's Free
            </button>
            {user?.affiliateStatus === 'active' && (
              <Link
                to="/affiliate/dashboard"
                className="btn btn-outline-light fw-semibold px-5 py-3 rounded-pill"
                style={{ fontSize: '16px' }}
              >
                Go to Dashboard
              </Link>
            )}
          </div>
          <p style={{ color: '#9b7dd4', fontSize: '13px', marginTop: '16px' }}>
            No followers minimum · No contract · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── Quick stats bar ── */}
      <section style={{ background: '#2d1b69', padding: '28px 24px' }}>
        <div className="container">
          <div className="row g-3 text-center text-white justify-content-center">
            {[
              { value: 'Up to 30%', label: 'Commission per conversion' },
              { value: '12 months', label: 'Recurring per customer' },
              { value: '$25', label: 'Minimum payout threshold' },
              { value: '26+ countries', label: 'Supported for payouts' },
            ].map(s => (
              <div className="col-6 col-md-3" key={s.label}>
                <div style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 700, fontFamily: "'Fredoka One',cursive", color: '#f9c74f' }}>{s.value}</div>
                <div style={{ fontSize: '13px', color: '#c9a8ff' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="container py-5" style={{ maxWidth: '900px' }}>
        <div className="text-center mb-5">
          <span className="badge rounded-pill px-3 py-2 mb-3" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', fontWeight: 700 }}>
            How it works
          </span>
          <h2 className="fw-bold" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,36px)', color: 'var(--text-main)' }}>
            Start earning in 4 simple steps
          </h2>
        </div>
        <div className="row g-4">
          {STEPS.map(step => (
            <div className="col-sm-6" key={step.num}>
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
                <div className="card-body p-4">
                  <div className="d-flex align-items-start gap-3">
                    <div style={{
                      width: '44px', height: '44px', background: '#6f42c1', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: '18px', flexShrink: 0,
                    }}>
                      {step.num}
                    </div>
                    <div>
                      <div style={{ fontSize: '22px', marginBottom: '6px' }}>{step.icon}</div>
                      <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '16px' }}>{step.title}</h6>
                      <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.6 }}>{step.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Commission example ── */}
      <section style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '60px 24px' }}>
        <div className="container text-center text-white" style={{ maxWidth: '640px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧮</div>
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,36px)' }}>
            See what you could earn
          </h2>
          <p style={{ color: '#c9a8ff', fontSize: '16px', marginBottom: '32px', lineHeight: 1.7 }}>
            Refer just 20 families on a monthly plan and earn passively every single month they stay subscribed.
          </p>
          <div className="row g-3 justify-content-center">
            {[
              { families: 10,  monthly: '$39+' },
              { families: 50,  monthly: '$195+' },
              { families: 100, monthly: '$390+' },
            ].map(e => (
              <div className="col-4" key={e.families}>
                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '20px 12px' }}>
                  <div style={{ fontSize: 'clamp(18px,3.5vw,28px)', fontWeight: 700, fontFamily: "'Fredoka One',cursive", color: '#f9c74f' }}>{e.monthly}</div>
                  <div style={{ fontSize: '12px', color: '#c9a8ff', marginTop: '4px' }}>{e.families} families / mo</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: '#9b7dd4', fontSize: '12px', marginTop: '20px' }}>
            Estimates based on 30% commission on a monthly Basic plan. Actual earnings vary.
          </p>
        </div>
      </section>

      {/* ── Perks ── */}
      <section className="container py-5" style={{ maxWidth: '960px' }}>
        <div className="text-center mb-5">
          <span className="badge rounded-pill px-3 py-2 mb-3" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', fontWeight: 700 }}>
            Why join us
          </span>
          <h2 className="fw-bold" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,36px)', color: 'var(--text-main)' }}>
            Everything you need to succeed
          </h2>
        </div>
        <div className="row g-4">
          {PERKS.map(perk => (
            <div className="col-md-4" key={perk.title}>
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px' }}>
                <div className="card-body p-4">
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>{perk.icon}</div>
                  <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{perk.title}</h6>
                  <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.6 }}>{perk.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Who is it for ── */}
      <section style={{ background: 'var(--card-bg)', padding: '60px 24px' }}>
        <div className="container" style={{ maxWidth: '820px' }}>
          <div className="text-center mb-5">
            <span className="badge rounded-pill px-3 py-2 mb-3" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', fontWeight: 700 }}>
              Who it's for
            </span>
            <h2 className="fw-bold" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,36px)', color: 'var(--text-main)' }}>
              Great for content creators & educators
            </h2>
          </div>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '📱', label: 'Parenting influencers' },
              { icon: '🎓', label: 'Teachers & educators' },
              { icon: '📝', label: 'Bloggers & writers' },
              { icon: '🎥', label: 'YouTubers & TikTokers' },
              { icon: '🎙️', label: 'Podcast hosts' },
              { icon: '📧', label: 'Newsletter creators' },
              { icon: '👩‍👧', label: 'Parent communities' },
              { icon: '📚', label: 'Children\'s book lovers' },
            ].map(item => (
              <div className="col-6 col-sm-4 col-md-3" key={item.label}>
                <div className="text-center p-3 rounded-3" style={{ background: 'var(--header-bg)', border: '1px solid var(--border-purple)' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>{item.icon}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="container py-5" style={{ maxWidth: '760px' }}>
        <div className="text-center mb-5">
          <span className="badge rounded-pill px-3 py-2 mb-3" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', fontWeight: 700 }}>
            FAQ
          </span>
          <h2 className="fw-bold" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,36px)', color: 'var(--text-main)' }}>
            Common questions
          </h2>
        </div>
        <div className="d-flex flex-column gap-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
                  {faq.q}
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.7 }}>
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '72px 24px' }}>
        <div className="container text-center text-white" style={{ maxWidth: '600px' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>🌙</div>
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(26px,5vw,42px)' }}>
            Ready to start earning?
          </h2>
          <p style={{ color: '#c9a8ff', fontSize: '17px', lineHeight: 1.7, marginBottom: '36px' }}>
            Join our affiliate program today. Applications are reviewed within 48 hours and there's no cost to join.
          </p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button
              className="btn btn-warning fw-bold px-5 py-3 rounded-pill"
              style={{ fontSize: '16px' }}
              onClick={handleApplyCta}
            >
              Apply Now — It's Free
            </button>
            <Link
              to="/contact"
              className="btn btn-outline-light fw-semibold px-5 py-3 rounded-pill"
              style={{ fontSize: '16px' }}
            >
              Have a question?
            </Link>
          </div>
          <p style={{ color: '#9b7dd4', fontSize: '13px', marginTop: '20px' }}>
            Already an affiliate?{' '}
            <Link to="/affiliate/dashboard" style={{ color: '#c9a8ff', fontWeight: 600 }}>
              Go to your dashboard →
            </Link>
          </p>
        </div>
      </section>

      {/* ── Footer note ── */}
      <div className="text-center py-4" style={{ background: 'var(--page-bg)' }}>
        <p className="text-muted small mb-1">
          Questions? Email us at{' '}
          <a href="mailto:support@dreamybedtime.com" style={{ color: '#6f42c1', fontWeight: 600 }}>
            support@dreamybedtime.com
          </a>
        </p>
        <p className="text-muted small mb-0">
          <Link to="/affiliate/terms" style={{ color: '#6f42c1' }}>Affiliate Programme Terms</Link>
          {' · '}
          <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy Policy</Link>
          {' · '}
          <Link to="/terms" style={{ color: '#6f42c1' }}>Terms of Service</Link>
        </p>
      </div>
    </div>
  );
};

export default AffiliateLandingPage;
